import SplitIO from '@splitsoftware/splitio/types/splitio'
import { SplitFactory } from '@splitsoftware/splitio'
import { datadogRum } from '@datadog/browser-rum'
import { isUiDev } from './ui.env'

interface Features {
  [featureName: string]: string
}

export function buildSplitClient(
  organizationUuid: string,
  features?: Features
): SplitIO.IClient {
  const splitFactory = SplitFactory({
    core: {
      authorizationKey: 'localhost',
      key: organizationUuid
    },
    features,
    startup: {
      readyTimeout: 1.5 // in seconds
    },
    impressionListener: {
      logImpression(impressionData) {
        datadogRum.addFeatureFlagEvaluation(
          impressionData.impression.feature,
          impressionData.impression.treatment
        )
      }
    }
  })

  const splitClient = splitFactory.client()

  // use a proxy to support override behavior
  const splitClientProxy = new Proxy(splitClient, {
    get(target, prop, receiver) {
      const value = target[prop]

      // override getTreatment() to allow flag overrides from URL in DEV environments
      // TODO: consider including QA environments
      if (prop === 'getTreatment' && isUiDev()) {
        return function (flagName: string) {
          // allow flag overrides from URL in non PROD environments
          // note: query comes before hash
          // e.g. http://...?PROD-XYZ_my_flag_description=on#...
          const searchParams = new URLSearchParams(window.location.search)
          if (searchParams.has(flagName)) {
            return searchParams.get(flagName)
          }

          // pass through to default behavior
          return value.apply(this === receiver ? target : this, [flagName])
        }
      }

      // pass through to default behavior
      if (value instanceof Function) {
        return function (...args) {
          return value.apply(this === receiver ? target : this, args)
        }
      }
      return value
    }
  })

  return splitClientProxy
}
